import React, { useState, useEffect } from 'react';
import { FaPlus } from 'react-icons/fa';
import styles from '../../../Configurations.module.css';
import componentStyles from './ClientTypes.module.css';
import { clientTypeService } from '../../../../services/clientTypeService';
import { useAlert } from '../../../../context/AlertContext';
import { useAccessToken } from '../../../../hooks/useAuth';
import ClientTypeForm from './ClientTypeForm';
import ClientTypeList from './ClientTypeList';
import ClientTypeStatistics from './ClientTypeStatistics';

const ClientTypes = () => {
    const { setErrorAlert, setInfoAlert } = useAlert();
    const { getBackendAccessToken } = useAccessToken();
    const [clientTypes, setClientTypes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [searchQuery, setSearchQuery] = useState('');
    const [statistics, setStatistics] = useState({
        total: 0,
        active: 0,
        inactive: 0
    });

    const [showForm, setShowForm] = useState(false);
    const [editingType, setEditingType] = useState(null);

    useEffect(() => {
        loadClientTypes();
    }, []);

    const loadClientTypes = async () => {
        try {
            setLoading(true);
            const accessToken = await getBackendAccessToken();
            const data = await clientTypeService.getAll(accessToken);
            
            if (Array.isArray(data)) {
                setClientTypes(data);
                setError(null);
                // Update statistics
                setStatistics({
                    total: data.length,
                    active: data.filter(type => type.isActive).length,
                    inactive: data.filter(type => !type.isActive).length
                });
            } else {
                throw new Error(`Invalid response format. Expected array, got ${typeof data}`);
            }
        } catch (err) {
            const errorMessage = 'Failed to load client types. Please try again later.';
            setError(errorMessage);
            setErrorAlert(errorMessage);
            console.error('Error loading client types:', err);
        } finally {
            setLoading(false);
        }
    };

    const handleFormSubmit = async (formData) => {
        try {
            const accessToken = await getBackendAccessToken();
            if (editingType) {
                await clientTypeService.update(accessToken, editingType.clientTypeId, formData);
                setInfoAlert('Client type updated successfully');
            } else {
                await clientTypeService.create(accessToken, formData);
                setInfoAlert('Client type created successfully');
            }
            await loadClientTypes();
            setShowForm(false);
            setEditingType(null);
            setError(null);
        } catch (err) {
            setError(err.message || 'Operation failed');
            console.error('Error handling client type:', err);
        }
    };

    const handleEdit = (type) => {
        setEditingType(type);
        setShowForm(true);
    };

    const handleDelete = async (clientTypeId) => {
        if (!window.confirm('Are you sure you want to delete this client type?')) {
            return;
        }

        try {
            const accessToken = await getBackendAccessToken();
            await clientTypeService.delete(accessToken, clientTypeId);
            await loadClientTypes();
            setInfoAlert('Client type deleted successfully');
            setError(null);
        } catch (err) {
            setErrorAlert(err.message || 'Failed to delete client type');
            // Trigger a refetch to clear the error state and reload the list
            // This ensures the UI returns to normal view after showing the error
            await loadClientTypes();
        }
    };

    const handleCancel = () => {
        setShowForm(false);
        setEditingType(null);
    };

    const filteredClientTypes = clientTypes.filter(type => {
        if (!searchQuery) return true;
        const query = searchQuery.toLowerCase();
        return (
            type.clientType.toLowerCase().includes(query) ||
            (type.clientTypeDescription && type.clientTypeDescription.toLowerCase().includes(query))
        );
    });

    return (
        <div className={styles.section}>
            <div className={styles.sectionHeader}>
                <h2>Client Types</h2>
                <p>Manage and configure different types of clients in the system.</p>
            </div>

            <div className={styles.content}>
                {/* Statistics Section */}
                {!loading && (
                    <ClientTypeStatistics 
                        statistics={statistics}
                        loading={loading}
                    />
                )}

                {/* Controls Section */}
                <div className={componentStyles.controls}>
                    <button
                        onClick={() => setShowForm(true)}
                        className={componentStyles.addButton}
                        disabled={showForm || loading}
                    >
                        <FaPlus className={componentStyles.buttonIcon} /> Create New Client Type
                    </button>
                </div>

                {/* Form Modal */}
                {showForm && (
                    <div className={componentStyles.modal}>
                        <div className={componentStyles.modalContent}>
                            <ClientTypeForm
                                clientType={editingType}
                                onSubmit={handleFormSubmit}
                                onCancel={handleCancel}
                                loading={loading}
                            />
                        </div>
                    </div>
                )}

                {/* Client Types List */}
                <ClientTypeList
                    clientTypes={filteredClientTypes}
                    loading={loading}
                    onEdit={handleEdit}
                    onDelete={handleDelete}
                    searchQuery={searchQuery}
                    setSearchQuery={setSearchQuery}
                />
            </div>
        </div>
    );
};

export default ClientTypes;
